import React, { useCallback, useState, useMemo, useRef, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useAppDispatch, useAppSelector } from '@hooks/index';
import { GIButton } from 'app-components';
import { ConditionalRendering } from '@components/common/conditional-rendering';
import ResultDialog from '@components/common/modals/ResultDialog';
import { uploadFeatureTemplate, setUploadTemplateSuccessMsg, setUploadTemplateErrorMsg, setUploadTemplateFailureMsg } from '@actions/featurePlan/featurePlanActionCreators';
import FeaturePlanList from '@modules/issuer-feature-plan/FeaturePlanList';
import AlertMessage from '@components/individualPortal/dashboard/AlertMsgs/AlertMessage';

const ALLOWED_FILE_TYPES = [ 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
const ALLOWED_EXT = '.xls,.xlsx,';

const FeaturePlanMainContent = () => {
    const dispatch = useAppDispatch();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { 
        uploadTemplateSuccessMessage, 
        uploadTemplateErrorMessage, 
        uploadTemplateFailureMessage, 
        encIssuerId,
        year
    } = useAppSelector(state => state.featurePlan);
    const years = [year, year + 1];
    const [ showUploadView, setShowUploadView ] = useState(false);
    const [ selectedYear, setSelectedYear ] = useState(year);
    const [ selectedFile, setSelectedFile ] = useState(null);
    const showUploadViewHandler = () => setShowUploadView(true);
    
    useEffect(() => {
        setSelectedYear(year);
    }, [year]);

    useEffect(() => {
        if(fileInputRef.current && uploadTemplateSuccessMessage.length) {
            fileInputRef.current.value = '';
            setSelectedFile(null);
        }
    }, [uploadTemplateSuccessMessage]);
    
    const hideUploadViewHandler = () => {
        setShowUploadView(false);
        dispatch(setUploadTemplateErrorMsg(''));
        dispatch(setUploadTemplateFailureMsg(''));
        dispatch(setUploadTemplateSuccessMsg(''));
        setSelectedFile(null);
        setSelectedYear(year);
    };

    const closeSuccessModalHandler = () => {
        hideUploadViewHandler();
    };
    
    const closeFailureModalHandler =() => {
        dispatch(setUploadTemplateFailureMsg(''));
        setSelectedFile(null);  
        if(fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const uploadTemplateHandler = useCallback(() => {
        if(selectedFile) {
            const formData = new FormData();
            formData.append('fileUploadFeaturedPlanTemplate', selectedFile as any);
            formData.append('field_name', selectedYear.toString());
            dispatch(uploadFeatureTemplate(encIssuerId, formData)); 
        }
    }, [selectedFile, selectedYear]);

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if(file) {
            const isValid = ALLOWED_FILE_TYPES.includes(file.type);
            if(!isValid) {
                dispatch(setUploadTemplateErrorMsg('issuer.featuretemplate.error.uploaddocument'));
                if(fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
                setSelectedFile(null);
            } else {
                setSelectedFile(file);
                dispatch(setUploadTemplateErrorMsg(''));
            }
        }
    };

    const isButtonDisabled = useMemo(() => { return !selectedFile; }, [ selectedFile ]);

    const uploadHeaderView = <>
        <h3 className="heading float-left"><FormattedMessage id='issuer.featureplan.uploadview.header' /></h3> 
        <div className="float-right margin-right-2 margin-top-1">
            <GIButton  type='button' id='btnCancelUploadView' labelId="button.cancel" variant='secondary' addlClassNames="margin-right-3" onClickHandler={hideUploadViewHandler} />
            <GIButton type='button'  id='btnUploadTemplate' labelId="button.save" variant='primary' onClickHandler={uploadTemplateHandler} isDisabled={isButtonDisabled} />
        </div>
    </>;

    const initHeaderView = <>
        <h3 className="heading float-left"><FormattedMessage id='issuer.featureplan.listview.header' /></h3>
        <div className="float-right margin-right-2 margin-top-1">
            <GIButton  type='button' id='btnShowUploadView' labelId={'Upload'} variant='secondary' onClickHandler={showUploadViewHandler} />
        </div>
    </>;

    return (
        <div className="grid-col grid-col-9 feature-plan-container">
            <ConditionalRendering isTrue={uploadTemplateSuccessMessage}>
                <ResultDialog message={<FormattedMessage id={uploadTemplateSuccessMessage}/>} headerId={'common.modal.success'} onClose={closeSuccessModalHandler} />
            </ConditionalRendering>
            <ConditionalRendering isTrue={uploadTemplateFailureMessage}>
                <ResultDialog message={<FormattedMessage id={uploadTemplateFailureMessage}/>} headerId={'common.modal.failure'} onClose={closeFailureModalHandler} />
            </ConditionalRendering>
            <ConditionalRendering isTrue={uploadTemplateErrorMessage}>
                <AlertMessage 
                    showIcon={false}
                    content={<FormattedMessage id={uploadTemplateErrorMessage}/>}
                    type="error" />
            </ConditionalRendering>
            <div className="header margin20-b">
                {!showUploadView && initHeaderView}
                {showUploadView && uploadHeaderView}
            </div>
            {showUploadView && <div id="featureTemplateFormHolder">
                <form>
                    <div className='margin-top-7'>
                        <div className="grid-row gi-component gi-regular_input">
                            <label htmlFor="ddFeaturePlanYear" className="tablet:grid-col-4 gi-regular_input__label gi-input iconed-false required-true"><FormattedMessage id='issuer.featureplan.uploadview.form.year.label' /></label>
                            <select name="featurePlanYear" id="ddFeaturePlanYear" data-test-id="feature_plan_year"
                                className="tablet:grid-col-6 gi-regular_input__select margin-top-2" 
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(Number(e.target.value))}>
                                {years.map((year) => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>
                        </div>
                        <div className="grid-row gi-component gi-regular_input margin-top-0" id="uploadTemplateHolder">
                            <label htmlFor="feature_plan_file_uploader" className="tablet:grid-col-4 gi-regular_input__label gi-input iconed-false required-true"><FormattedMessage id='issuer.featureplan.uploadview.form.file.label' /></label>
                            <input id="featurePlanFile" data-test-id="feature_plan_file_uploader" ref={fileInputRef} className="usa-file-input margin-top-5" type="file" accept={ALLOWED_EXT} name="featurePlanFile" onChange={handleFileUpload}/>
                        </div>
                    </div>
                </form>
            </div>
            }
            <FeaturePlanList showUploadView={showUploadView}/>
        </div>    
    );
};

export default FeaturePlanMainContent;
